// Mini Player Component - Like SoundCloud/BeatStars
// Always visible at the bottom of the screen
import React, { useEffect, useRef, useState, useMemo, useCallback, memo } from 'react';
import { Play, Pause, Volume2, VolumeX, X } from 'lucide-react';
import { useAudioStore } from '@stores/useAudioStore';
import { useFileStore } from '@stores/useFileStore';
import { formatTime } from '@utils/format';
import { cn } from '@utils/classNames';

export const MiniPlayer: React.FC = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const {
    currentFile,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    setIsPlaying,
    setCurrentTime,
    setDuration,
    setAudioUrl,
    stop,
  } = useAudioStore();
  const { getBlobUrl } = useFileStore();
  const [audioUrl, setLocalAudioUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const currentFileIdRef = useRef<string | null>(null); // Track current file ID to prevent unnecessary reloads
  const audioLoadRequestIdRef = useRef(0); // Guard against stale async getBlobUrl results
  const timeUpdateIntervalRef = useRef<number | null>(null); // Fallback polling for time updates
  const isSeekingRef = useRef(false); // Track if user is seeking to prevent conflicts

  // Load audio file when currentFile changes - optimized to avoid full reload
  useEffect(() => {
    if (!currentFile) {
      // Clean up polling interval
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current.currentTime = 0;
      }
      setLocalAudioUrl(null);
      setCurrentTime(0);
      setIsLoading(false);
      currentFileIdRef.current = null;
      isSeekingRef.current = false;
      return;
    }

    // Only reload if the file ID actually changed
    if (currentFileIdRef.current === currentFile.id) {
      // Same file, don't reload - audio should continue playing
      return;
    }

    // File ID changed - load new audio
    currentFileIdRef.current = currentFile.id;
    const requestId = ++audioLoadRequestIdRef.current;

    // CRITICAL: Stop previous audio IMMEDIATELY and synchronously before loading new one
    // This must happen before any async operations to prevent old track from continuing
    if (audioRef.current) {
      // Immediately pause and stop - this is synchronous and happens instantly
      const audio = audioRef.current;
      audio.pause();
      audio.currentTime = 0;
      
      // Clear source immediately to stop any buffered playback
      audio.src = '';
      audio.load(); // Force reset to clear any buffered data
      
      // Also clear local URL state immediately
      setLocalAudioUrl(null);
      setAudioUrl(null);
    }

    // Don't set loading state immediately - only set it when we actually start loading
    // This prevents UI flicker
    const loadAudio = async () => {
      // Small delay to prevent flicker - only show loading if it takes longer
      const loadingTimeout = setTimeout(() => {
        // Only show loading state if this is still the latest request
        if (requestId === audioLoadRequestIdRef.current) {
          setIsLoading(true);
        }
      }, 100);

      try {
        const url = await getBlobUrl(currentFile.id);
        clearTimeout(loadingTimeout);

        // Ignore stale requests (track switched while we were fetching)
        if (requestId !== audioLoadRequestIdRef.current) return;
        
        if (url) {
          setLocalAudioUrl(url);
          setAudioUrl(url);
          setIsLoading(false); // Clear loading state immediately after URL is set
          // Note: isPlaying is already set to true by play() function
          // The audio will auto-play once loaded (see handleLoadedMetadata/handleCanPlay)
        } else {
          console.error('No URL returned for file:', currentFile.id);
          // Ignore stale requests (track switched while we were fetching)
          if (requestId !== audioLoadRequestIdRef.current) return;
          setIsLoading(false);
          setIsPlaying(false); // Reset playing state if no URL
        }
      } catch (error) {
        clearTimeout(loadingTimeout);

        // Ignore stale requests (track switched while we were fetching)
        if (requestId !== audioLoadRequestIdRef.current) return;

        console.error('Failed to load audio:', error);
        setIsLoading(false);
        setIsPlaying(false); // Reset playing state on error
      }
    };

    loadAudio();
  }, [currentFile?.id, getBlobUrl, setAudioUrl, setCurrentTime, setIsPlaying]);

  // Handle page visibility changes (mobile fix - prevent audio from stopping)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!audioRef.current || !currentFile) return;
      
      if (document.hidden) {
        // Page is hidden - don't pause, just ensure audio continues
        // On mobile, audio should continue playing in background
        console.log('📱 Page hidden, audio should continue playing');
      } else {
        // Page is visible again - resume if it was playing
        if (isPlaying && audioRef.current.paused && audioRef.current.readyState >= 2) {
          audioRef.current.play().catch((error) => {
            console.error('Failed to resume after visibility change:', error);
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Handle page focus/blur for mobile browsers
    const handleFocus = () => {
      if (!audioRef.current || !currentFile) return;
      if (isPlaying && audioRef.current.paused && audioRef.current.readyState >= 2) {
        audioRef.current.play().catch((error) => {
          console.error('Failed to resume on focus:', error);
        });
      }
    };

    const handleBlur = () => {
      // Don't pause on blur - let audio continue playing
      console.log('📱 Page blurred, audio continues');
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [currentFile, isPlaying]);

  // Initialize audio element - simple and reliable
  useEffect(() => {
    if (!audioUrl || !audioRef.current || !currentFile) return;

    const audio = audioRef.current;
    
    // Check if we already have audio loaded for this file ID AND the same URL
    // Only skip reload if it's the exact same file with the same URL
    const hasAudioLoaded = audio.src && 
                          audio.src === audioUrl && 
                          audio.src !== '' && 
                          currentFileIdRef.current === currentFile.id &&
                          audio.readyState > 0; // Audio has loaded some data
    
    if (hasAudioLoaded) {
      // Same file with same URL, audio already loaded - don't reload to prevent interruption
      return;
    }
    
    // Only set source if audio is not loaded or it's a different file
    // Always reset when switching tracks to ensure clean state
    if (audio.src !== audioUrl || currentFileIdRef.current !== currentFile.id) {
      // Completely stop and reset audio element
      audio.pause();
      audio.currentTime = 0;
      
      // Clear old source first to ensure clean transition
      if (audio.src && audio.src !== audioUrl) {
        audio.src = '';
        audio.load(); // Force clear old audio
      }
      
      // Set new source and load it
      audio.src = audioUrl;
      audio.load(); // Explicitly load new audio to ensure it starts fresh
    }

    // Event listeners - always register to ensure they work
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false); // Metadata loaded, audio is ready
      // Auto-play if isPlaying is true (when file was just set)
      const currentIsPlaying = useAudioStore.getState().isPlaying;
      if (currentIsPlaying && audio.paused) {
        audio.play().catch((error) => {
          console.error('Failed to auto-play:', error);
          setIsPlaying(false);
        });
      }
    };

    const handleCanPlay = () => {
      setIsLoading(false); // Audio is ready to play
      // Also try to play when audio is ready to play
      const currentIsPlaying = useAudioStore.getState().isPlaying;
      if (currentIsPlaying && audio.paused) {
        audio.play().catch((error) => {
          console.error('Failed to play on canplay:', error);
          setIsPlaying(false);
        });
      }
    };

    const handleTimeUpdate = () => {
      // Always update time from audio element, unless user is actively seeking
      if (!isSeekingRef.current && audio && audio.readyState > 0) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handleEnded = () => {
      useAudioStore.getState()._notifyPlaybackEnded();
      if (useAudioStore.getState().currentlyPlaying) return;
      setIsPlaying(false);
      setCurrentTime(0);
      stop();
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    // If audio is already loaded and should be playing, start it immediately
    const currentIsPlaying = useAudioStore.getState().isPlaying;
    if (audio.readyState >= 2 && currentIsPlaying && audio.paused) {
      audio.play().catch((error) => {
        console.error('Failed to play already loaded audio:', error);
        setIsPlaying(false);
      });
    } else if (audio.readyState >= 1 && currentIsPlaying && audio.paused) {
      // Even if only metadata is loaded, try to play (browser will buffer)
      audio.play().catch((error) => {
        console.error('Failed to play audio (buffering):', error);
        // Don't set isPlaying to false yet, wait for canplay
      });
    }

    // Handle audio errors (mobile fix)
    const handleError = (e: Event) => {
      console.error('Audio error:', e);
      const audio = e.target as HTMLAudioElement;
      if (audio.error) {
        console.error('Audio error code:', audio.error.code, 'message:', audio.error.message);
        // Try to recover - reload the audio
        if (audio.src) {
          const currentTime = audio.currentTime;
          audio.load();
          audio.addEventListener('loadedmetadata', () => {
            audio.currentTime = Math.min(currentTime, audio.duration);
            const currentIsPlaying = useAudioStore.getState().isPlaying;
            if (currentIsPlaying) {
              audio.play().catch((err) => console.error('Failed to resume after error:', err));
            }
          }, { once: true });
        }
      }
    };

    // Handle audio stalled (mobile fix - network issues)
    const handleStalled = () => {
      console.log('Audio stalled, trying to resume...');
      const currentIsPlaying = useAudioStore.getState().isPlaying;
      if (currentIsPlaying && audio.paused) {
        audio.play().catch((err) => console.error('Failed to resume after stall:', err));
      }
    };

    // Handle audio suspend (mobile fix - browser suspends audio)
    const handleSuspend = () => {
      console.log('Audio suspended');
      // Don't change state - browser will resume when ready
    };

    audio.addEventListener('error', handleError);
    audio.addEventListener('stalled', handleStalled);
    audio.addEventListener('suspend', handleSuspend);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('stalled', handleStalled);
      audio.removeEventListener('suspend', handleSuspend);
    };
  }, [audioUrl, setCurrentTime, setDuration, setIsPlaying, stop, currentFile]);

  // Separate effect for time updates with fallback polling mechanism
  // This ensures time updates work even if timeupdate event doesn't fire reliably
  useEffect(() => {
    if (!audioRef.current || !currentFile) {
      // Clear interval if no audio
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
      return;
    }

    const audio = audioRef.current;
    
    // Fallback polling mechanism - updates time every 100ms when playing
    // This ensures the UI updates even if timeupdate event doesn't fire
    const startPolling = () => {
      // Clear any existing interval first
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
      
      timeUpdateIntervalRef.current = window.setInterval(() => {
        // Check if audio element still exists and is valid
        if (!audioRef.current || !currentFile) {
          if (timeUpdateIntervalRef.current) {
            clearInterval(timeUpdateIntervalRef.current);
            timeUpdateIntervalRef.current = null;
          }
          return;
        }
        
        const currentAudio = audioRef.current;
        // Update time if audio is playing (not paused) and not seeking
        if (!currentAudio.paused && !isSeekingRef.current) {
          const newTime = currentAudio.currentTime;
          // Always update to ensure UI stays in sync
          setCurrentTime(newTime);
        }
      }, 100);
    };

    const stopPolling = () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
    };

    // Start polling when playing, stop when paused
    // Check both isPlaying state and actual audio.paused state
    // Also check if audio is ready to play
    const shouldPoll = isPlaying || (!audio.paused && audio.readyState > 0);
    
    if (shouldPoll) {
      // Start polling immediately, but also set up a check to restart if needed
      startPolling();
      
      // Also listen to play events to ensure polling starts
      const handlePlayForPolling = () => {
        if (!timeUpdateIntervalRef.current) {
          startPolling();
        }
      };
      
      audio.addEventListener('play', handlePlayForPolling);
      
      return () => {
        audio.removeEventListener('play', handlePlayForPolling);
        stopPolling();
      };
    } else {
      stopPolling();
      
      return () => {
        stopPolling();
      };
    }
  }, [isPlaying, currentFile, setCurrentTime, audioUrl]);

  // Update volume when it changes - separate effect to prevent audio reload
  useEffect(() => {
    if (audioRef.current) {
      // Only update volume, don't touch anything else
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const togglePlayPause = useCallback(() => {
    if (!audioRef.current) return;
    
    // Allow play/pause even while loading - audio element handles it
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      // Try to play even if loading - audio will queue it
      if (audioRef.current.readyState >= 2) {
        // Audio has enough data to play
        audioRef.current.play().catch((error) => {
          console.error('Failed to play:', error);
          setIsPlaying(false);
        });
        setIsPlaying(true);
      } else {
        // Audio not ready yet, set playing state and wait for canplay
        setIsPlaying(true);
        setIsLoading(true);
      }
    }
  }, [isPlaying, setIsPlaying]);

  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      isSeekingRef.current = true;
      audioRef.current.currentTime = time;
      setCurrentTime(time);
      // Reset seeking flag after a short delay to allow timeupdate to resume
      setTimeout(() => {
        isSeekingRef.current = false;
      }, 200);
    }
  }, [setCurrentTime]);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    const audioState = useAudioStore.getState();
    audioState.setVolume(newVolume);
    if (audioState.isMuted && newVolume > 0) {
      audioState.toggleMute();
    }
  }, []);

  const toggleMute = useCallback(() => {
    useAudioStore.getState().toggleMute();
  }, []);

  // Sync play/pause with audio element - CRITICAL: must stop immediately when isPlaying is false
  useEffect(() => {
    if (!audioRef.current) return;

    // If isPlaying is false, IMMEDIATELY pause (but keep current position)
    // This must happen synchronously to prevent old track from continuing
    if (!isPlaying) {
      // Immediately pause - this is synchronous
      // Don't reset currentTime here - only pause to preserve position
      audioRef.current.pause();
      return;
    }

    // Only try to play if audio URL is set
    if (!audioUrl) return;

    // Only auto-play if audio has loaded enough data
    if (audioRef.current.readyState >= 2) {
      // Audio has enough data to play
      audioRef.current.play().catch((error) => {
        console.error('Failed to play:', error);
        setIsPlaying(false);
      });
    }
    // If readyState < 2, wait for loadedmetadata/canplay events to handle playback
  }, [isPlaying, setIsPlaying, audioUrl]);

  // Keyboard shortcut: Spacebar to play/pause
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle spacebar if MiniPlayer has a current file
      if (!currentFile) return;
      
      // Don't trigger if user is typing in an input field
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Spacebar pressed
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        togglePlayPause();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentFile, togglePlayPause]);

  // Memoize callbacks to prevent unnecessary re-renders
  const handleClose = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    stop();
  }, [stop]);

  // Memoize progress calculations
  const progressMax = useMemo(
    () => (Number.isFinite(duration) && duration > 0 ? duration : Math.max(currentTime, 0)),
    [duration, currentTime]
  );
  const progressValue = useMemo(
    () => Math.min(currentTime, progressMax),
    [currentTime, progressMax]
  );

  // Memoize track metadata
  const trackMetadata = useMemo(
    () => ({
      name: currentFile?.name || '',
      thumbnailUrl: currentFile?.thumbnailUrl,
      bpm: currentFile?.bpm,
      key: currentFile?.key,
    }),
    [currentFile?.name, currentFile?.thumbnailUrl, currentFile?.bpm, currentFile?.key]
  );

  // Don't render if no file is playing
  if (!currentFile) {
    return null;
  }

  return (
    <>
      {/* Hidden audio element - preload auto for faster loading */}
      <audio ref={audioRef} preload="auto" />
      
      {/* Mini Player Bar - Modern flat design, no gloss / no glow */}
      <div data-miniplayer className="fixed bottom-0 left-0 right-0 z-[120] bg-dark-secondary border-t border-white/[0.08] sidebar-offset-miniplayer">
        <div className="max-w-[min(100%,theme(maxWidth.7xl))] mx-auto px-3 sm:px-4 py-2 sm:py-2.5">
          <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 md:gap-4">
            {/* Play/Pause Button */}
            <PlayPauseButton
              isPlaying={isPlaying}
              isLoading={isLoading}
              onClick={togglePlayPause}
            />

            {/* Track Info */}
            <TrackInfo
              name={trackMetadata.name}
              thumbnailUrl={trackMetadata.thumbnailUrl}
              bpm={trackMetadata.bpm}
              key={trackMetadata.key}
            />

            {/* Progress */}
            <ProgressBar
              currentTime={currentTime}
              duration={duration}
              progressMax={progressMax}
              progressValue={progressValue}
              onSeek={handleSeek}
            />

            {/* Volume */}
            <VolumeControl
              volume={volume}
              isMuted={isMuted}
              onVolumeChange={handleVolumeChange}
              onToggleMute={toggleMute}
            />

            {/* Close Button */}
            <button
              onClick={handleClose}
              className="order-5 hidden sm:flex flex-shrink-0 p-1.5 text-white/40 hover:text-white hover:bg-white/[0.06] rounded-lg transition-colors touch-manipulation"
              title="Close player"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Memoized sub-components to prevent unnecessary re-renders
const PlayPauseButton = memo<{
  isPlaying: boolean;
  isLoading: boolean;
  onClick: () => void;
}>(({ isPlaying, isLoading, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      "order-1 flex-shrink-0 w-11 h-11 sm:w-10 sm:h-10 flex items-center justify-center bg-primary hover:bg-primary/90 active:bg-primary/80 rounded-lg text-black transition-colors touch-manipulation",
      isLoading && "opacity-70"
    )}
  >
    {isLoading ? (
      <span className="animate-spin text-sm sm:text-xs">⏳</span>
    ) : isPlaying ? (
      <Pause className="w-[18px] h-[18px] sm:w-4 sm:h-4 fill-current" />
    ) : (
      <Play className="w-[18px] h-[18px] sm:w-4 sm:h-4 fill-current ml-0.5" />
    )}
  </button>
));
PlayPauseButton.displayName = 'PlayPauseButton';

const TrackInfo = memo<{
  name: string;
  thumbnailUrl?: string;
  bpm?: number;
  key?: string;
}>(({ name, thumbnailUrl, bpm, key }) => (
  <div className="order-2 flex items-center gap-2.5 sm:gap-3 flex-1 min-w-[160px]">
    <div className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 bg-white/[0.06] rounded-lg overflow-hidden">
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={name}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-white/[0.06]">
          <span className="text-white/40 text-xs">🎵</span>
        </div>
      )}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-white font-inter font-semibold text-sm leading-tight truncate">
        {name}
      </p>
      <p className="text-white/50 font-inter text-[11px] truncate">
        {bpm ? `${bpm} BPM` : ''}
        {key && ` • ${key}`}
      </p>
    </div>
  </div>
));
TrackInfo.displayName = 'TrackInfo';

const ProgressBar = memo<{
  currentTime: number;
  duration: number;
  progressMax: number;
  progressValue: number;
  onSeek: (e: React.ChangeEvent<HTMLInputElement>) => void;
}>(({ currentTime, duration, progressMax, progressValue, onSeek }) => (
  <div className="order-4 sm:order-3 flex items-center gap-1.5 sm:gap-2 flex-1 min-w-[200px]">
    <span className="text-white/50 font-codec text-[10px] w-10 text-right tabular-nums flex-shrink-0">
      {formatTime(currentTime)}
    </span>
    <input
      type="range"
      min={0}
      max={progressMax || 0}
      step="0.1"
      value={progressValue}
      onChange={onSeek}
      className="flex-1 h-1 bg-white/[0.08] rounded-full appearance-none cursor-pointer hover:bg-white/[0.12] transition-colors touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-secondary [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
    />
    <span className="text-white/40 font-codec text-[10px] w-10 tabular-nums flex-shrink-0 text-left">
      {formatTime(duration)}
    </span>
  </div>
));
ProgressBar.displayName = 'ProgressBar';

const VolumeControl = memo<{
  volume: number;
  isMuted: boolean;
  onVolumeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onToggleMute: () => void;
}>(({ volume, isMuted, onVolumeChange, onToggleMute }) => (
  <div className="order-3 sm:order-4 flex items-center gap-1.5">
    <button
      onClick={onToggleMute}
      className="flex-shrink-0 p-1.5 text-white/50 hover:text-white hover:bg-white/[0.06] rounded-lg transition-colors touch-manipulation"
      aria-label={isMuted || volume === 0 ? "Unmute" : "Mute"}
    >
      {isMuted || volume === 0 ? (
        <VolumeX className="w-4 h-4" />
      ) : (
        <Volume2 className="w-4 h-4" />
      )}
    </button>
    <input
      type="range"
      min="0"
      max="1"
      step="0.01"
      value={isMuted ? 0 : volume}
      onChange={onVolumeChange}
      className="w-20 sm:w-24 h-1 bg-white/[0.08] rounded-full appearance-none cursor-pointer hover:bg-white/[0.12] transition-colors touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-secondary [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 [&::-webkit-slider-thumb]:h-2.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white/70 [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-2.5 [&::-moz-range-thumb]:h-2.5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white/70 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
    />
  </div>
));
VolumeControl.displayName = 'VolumeControl';

